import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { Button, Modal } from '@ibk/shared-ui';
import { BenefitsOnboardingSpotlight } from './BenefitsOnboardingSpotlight';
import { benefitsOnboardingWizardTestIds } from './benefitsOnboardingTestIds';
import { LEGACY_BENEFITS_ONBOARDING_TOUR_CONFIG } from './benefitsOnboardingTourConfig';
import styles from './BenefitsOnboardingWizard.module.css';
function resolveInitialPhase(startPhase) {
    if (startPhase === 'skipConfirm') {
        return 'skipConfirm';
    }
    return 'tour';
}
export function BenefitsOnboardingWizard({ active = true, onFlowFinished, 'data-testid': testIdProp, startPhase: startPhaseProp = 'tour', onReopenFromSkipCancel, tourConfig = LEGACY_BENEFITS_ONBOARDING_TOUR_CONFIG, onTourStepChange, }) {
    const { t } = useTranslation();
    const ids = useMemo(() => benefitsOnboardingWizardTestIds(testIdProp), [testIdProp]);
    const [phase, setPhase] = useState(() => resolveInitialPhase(startPhaseProp));
    const [tourStepIndex, setTourStepIndex] = useState(0);
    const skipConfirmOpenedFromTourRef = useRef(false);
    const finishFlow = useCallback((reason) => {
        setPhase('done');
        onFlowFinished?.(reason);
    }, [onFlowFinished]);
    const goToSkipConfirm = useCallback(() => {
        setPhase(current => {
            skipConfirmOpenedFromTourRef.current = current === 'tour';
            return 'skipConfirm';
        });
    }, []);
    const handleSkipConfirm = useCallback(() => {
        finishFlow('skipped');
    }, [finishFlow]);
    const handleSkipCancel = useCallback(() => {
        if (skipConfirmOpenedFromTourRef.current) {
            skipConfirmOpenedFromTourRef.current = false;
            setPhase('tour');
            return;
        }
        if (onReopenFromSkipCancel) {
            onReopenFromSkipCancel();
            setPhase('done');
            return;
        }
        setTourStepIndex(0);
        setPhase('tour');
    }, [onReopenFromSkipCancel]);
    const handleTourBack = useCallback(() => {
        if (tourStepIndex <= 0) {
            return;
        }
        setTourStepIndex(i => i - 1);
    }, [tourStepIndex]);
    const handleTourForward = useCallback(() => {
        if (tourStepIndex >= tourConfig.stepCount - 1) {
            finishFlow('completed');
            return;
        }
        setTourStepIndex(i => i + 1);
    }, [tourStepIndex, finishFlow, tourConfig.stepCount]);
    const isTourVisible = active && phase === 'tour';
    useEffect(() => {
        onTourStepChange?.(isTourVisible ? tourStepIndex : null);
    }, [isTourVisible, tourStepIndex, onTourStepChange]);
    const displayStep = tourStepIndex + 1;
    const tourTitleKey = `${tourConfig.translationKeyPrefix}.step${displayStep}.title`;
    const tourBodyKey = `${tourConfig.translationKeyPrefix}.step${displayStep}.body`;
    const targetSelector = tourConfig.stepSelectors[tourStepIndex] ?? '';
    const forwardLabel = useMemo(() => tourStepIndex >= tourConfig.stepCount - 1
        ? t('benefits.onboarding.tour.finish')
        : t('benefits.onboarding.tour.forward'), [t, tourStepIndex, tourConfig.stepCount]);
    if (!active || phase === 'done') {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx(Modal, { isOpen: phase === 'skipConfirm', onClose: handleSkipCancel, title: t('benefits.onboarding.skipConfirm.title'), showCloseButton: true, closeButtonAriaLabel: t('benefits.onboarding.skipConfirm.closeAriaLabel'), closeOnBackdropClick: false, className: styles.skipConfirmModal, bodyClassName: styles.skipConfirmBody, "data-testid": ids.skipConfirm, children: _jsxs("div", { className: styles.skipConfirmContent, children: [_jsx("p", { className: styles.skipConfirmDescription, children: t('benefits.onboarding.skipConfirm.description') }), _jsxs("div", { className: styles.skipConfirmActions, children: [_jsx(Button, { variant: 'outline', size: 'large', onClick: handleSkipCancel, dataTestId: ids.skipCancel, children: t('benefits.onboarding.skipConfirm.cancel') }), _jsx(Button, { variant: 'primary', size: 'large', onClick: handleSkipConfirm, dataTestId: ids.skipConfirm, children: t('benefits.onboarding.skipConfirm.confirm') })] })] }) }), phase === 'tour' && (_jsx(BenefitsOnboardingSpotlight, { stepIndex: tourStepIndex, totalSteps: tourConfig.stepCount, targetSelector: targetSelector, resolveTargetElement: tourConfig.resolveTargetElementForStep
                    ? () => tourConfig.resolveTargetElementForStep?.(tourStepIndex) ?? null
                    : undefined, extraHighlightSelector: tourConfig.extraHighlightSelectors?.[tourStepIndex], title: t(tourTitleKey), body: t(tourBodyKey), onBack: handleTourBack, onForward: handleTourForward, forwardLabel: forwardLabel, backDisabled: tourStepIndex === 0, onRequestClose: goToSkipConfirm, panelHorizontalAlign: tourConfig.panelHorizontalAlign[tourStepIndex] ?? 'center', arrowXMode: tourConfig.arrowXMode[tourStepIndex] ?? 'hole-center', panelPlacement: tourConfig.panelPlacement[tourStepIndex] ?? 'stack', panelMaxWidthPx: tourConfig.panelMaxWidthPx[tourStepIndex] ?? tourConfig.panelDefaultMaxWidthPx, panelLeadingVerticalAlign: tourConfig.leadingVerticalAlign[tourStepIndex] ?? 'center', "data-testid": ids.spotlight }))] }));
}
